import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import { H4, Body } from '@meiuca/ui-components'
import { Loader2, Wallet } from 'lucide-react'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { PlanCard } from '@/components/PlanCard'
import { StudentFilterPills, buildStudentFilterOptions } from '@/components/StudentFilterPills'
import { useAuth } from '@/contexts/AuthContext'
import { LoginRequiredEmptyState } from '@/components/LoginRequiredEmptyState'
import { PendingTermsBanners } from '@/components/PendingTermsBanners'
import {
  trainingCenterPlanService,
  type PlanListItem,
  type TrainingCenterTermsItem
} from '@/services/trainingCenterPlanService'

export function PlansPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()

  const [plans, setPlans] = useState<PlanListItem[]>([])
  const [terms, setTerms] = useState<TrainingCenterTermsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Filtro por pessoa (responsável com dependentes). Lista curta e não
  // paginada → filtro client-side; as pílulas só existem com >1 aluno visível.
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null)

  const studentOptions = buildStudentFilterOptions(plans)
  const visiblePlans = selectedStudentId
    ? plans.filter(plan => plan.studentCustomer?.id === selectedStudentId)
    : plans

  useEffect(() => {
    analyticsService.trackScreenView(SCREEN_NAMES.PLANS)
  }, [])

  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      analyticsService.track(ANALYTICS_EVENTS.PLAN_LOGIN_REQUIRED, {})
    }
  }, [isAuthLoading, isAuthenticated])

  useEffect(() => {
    // Show the login empty state when logged out instead of redirecting away.
    if (isAuthenticated) {
      loadPlans()
      // Best-effort: a failure here must never hide the plans.
      trainingCenterPlanService
        .listTerms()
        .then(setTerms)
        .catch(() => setTerms([]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  const loadPlans = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await trainingCenterPlanService.listPlans({ status: 'active' })
      setPlans(result)
    } catch (err) {
      console.error('Error loading plans:', err)
      setError('Erro ao carregar seus planos. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <H4 className="text-text-primary font-semibold mb-2">Meus Planos</H4>
          <Body className="text-text-secondary">
            Seus planos ativos nos centros de treinamento
          </Body>
        </div>

        {isAuthLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
          </div>
        ) : !isAuthenticated ? (
          <LoginRequiredEmptyState
            title="Entre para ver seus planos"
            description="Faça login para acompanhar seus planos nos centros de treinamento."
          />
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
            <Body className="text-text-secondary mt-4">Carregando planos...</Body>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Body className="text-feedback-error mb-4">{error}</Body>
            <button
              onClick={loadPlans}
              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        ) : plans.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 mb-4 rounded-full bg-background-secondary flex items-center justify-center">
              <Wallet className="w-8 h-8 text-text-tertiary" />
            </div>
            <H4 className="text-text-primary mb-2">Nenhum plano ativo</H4>
            <Body className="text-text-secondary text-center max-w-md">
              Você não tem planos ativos em centros de treinamento no momento.
            </Body>
          </div>
        ) : (
          <>
            <PendingTermsBanners items={terms} />
            <StudentFilterPills
              options={studentOptions}
              selectedId={selectedStudentId}
              onSelect={id => {
                setSelectedStudentId(id)
                analyticsService.track(ANALYTICS_EVENTS.PLANS_STUDENT_FILTER_CHANGED, {
                  student_customer_id: id ?? 'all'
                })
              }}
            />
            <div className="space-y-4">
              {visiblePlans.map((plan, index) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  position={index}
                  showStudent={studentOptions.length > 0}
                  onClick={() => {
                    analyticsService.track(ANALYTICS_EVENTS.PLAN_CARD_CLICKED, { plan_id: plan.id, position: index, outstanding_count: plan.outstandingCount })
                    navigate(`/planos/${plan.id}`)
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
